import {ElevationApp} from "../ElevationApp";
import {Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle} from "@mui/material";
import {useEffect, useMemo, useReducer, useState} from "react";
import {FrmVar} from "../drawfrm/FrmVar";
import {frameProfileHasTabs, MdFrameSpec} from "../model/frame/MdFrameSpec";
import {hasThreshold} from "../model/frame/MdFrameBlock";
import {referencedProfileKeys} from "../scenes/frameProfile/profileVarRefs";
import {DimensionEditor, formatDim} from "../components/DimensionEditor";
import {UnitSystem} from "../../../../../api/Projects";

// a numeric (dimension) property backed by a FrmVar
type NumericProp = {
    key: string;
    label: string;
    v: FrmVar;
};

// a non-numeric config property rendered read-only
type TextProp = {
    key: string;
    label: string;
    value: string;
};

// keys only meaningful on a pair opening
const PAIR_KEYS = new Set(["inActiveDoorWidth", "pairDoorGap"]);

// keys only meaningful on a tabbed profile. Gated by frameProfileHasTabs rather
// than the drawing reference check: the tabs feed the calculated throatSize even
// on profiles whose cross-section doesn't dimension them.
const TAB_KEYS = new Set(["topTab", "bottomTab"]);

// keys whose relevance the current profile's cross-section drawing decides
// (see referencedProfileKeys)
const PROFILE_DRAWING_KEYS = new Set([
    "rabbetDepth", "bottomRabbetDepth",
    "doubleEgressLip", "doubleEgressStep1", "doubleEgressStep2", "doubleEgressSmallFace",
    "drywallLip", "drywallHookDrop", "kerfDepth", "expFace",
]);

// hide properties the current opening situation can't use: pair-only widths on a
// single door, profile-cut dims the current profile's drawing never references,
// the threshold height when no threshold sill exists. profileKeys=null means the
// profile has no drawing — relevance is unknown, so show everything.
function isRelevant(key: string, spec: MdFrameSpec, profileKeys: Set<string> | null): boolean {
    if(PAIR_KEYS.has(key)) return spec.isPair;
    if(TAB_KEYS.has(key)) return frameProfileHasTabs.has(spec.profile);
    if(PROFILE_DRAWING_KEYS.has(key)) return profileKeys ? profileKeys.has(key) : true;
    if(key === "thresholdHeight") return hasThreshold(spec.frameBlocks);
    return true;
}

function numericProps(spec: MdFrameSpec, profileKeys: Set<string> | null): NumericProp[] {
    const all: NumericProp[] = [
        {key: "nominalWidth", label: "Nominal Width", v: spec.nominalWidth},
        {key: "openingWidth", label: "Opening Width", v: spec.openingWidth},
        {key: "innerWidth", label: "Inner Width", v: spec.innerWidth},
        {key: "innerHeight", label: "Inner Height", v: spec.nominalHeight},
        {key: "activeDoorWidth", label: "Active Door Width", v: spec.activeDoorWidth},
        {key: "inActiveDoorWidth", label: "Inactive Door Width", v: spec.inActiveDoorWidth},
        {key: "pairDoorGap", label: "Pair Door Gap", v: spec.pairDoorGap},
        {key: "doorThickness", label: "Door Thickness", v: spec.doorFrameProfile.doorThickness},
        {key: "jambDepth", label: "Jamb Depth", v: spec.jambDepth},
        {key: "jambFace", label: "Jamb Face", v: spec.jambFace},
        {key: "thresholdHeight", label: "Threshold Height", v: spec.thresholdHeight},
        {key: "throatSize", label: "Throat Size", v: spec.throatSize},
        {key: "frameStopToDoorGap", label: "Frame Stop → Door Gap", v: spec.frameStopToDoorGap},
        {key: "frameJambToDoorGap", label: "Frame Jamb → Door Gap", v: spec.frameJambToDoorGap},
        {key: "rabbetDepth", label: "Rabbet Depth", v: spec.doorFrameProfile.rabbetDepth},
        {key: "bottomRabbetDepth", label: "Bottom Rabbet Depth", v: spec.doorFrameProfile.bottomRabbetDepth},
        {key: "topTab", label: "Top Tab", v: spec.doorFrameProfile.topTab},
        {key: "bottomTab", label: "Bottom Tab", v: spec.doorFrameProfile.bottomTab},
        {key: "doubleEgressLip", label: "Double Egress Lip", v: spec.doorFrameProfile.doubleEgressLip},
        {key: "doubleEgressStep1", label: "Double Egress Step 1", v: spec.doorFrameProfile.doubleEgressStep1},
        {key: "doubleEgressStep2", label: "Double Egress Step 2", v: spec.doorFrameProfile.doubleEgressStep2},
        {key: "doubleEgressSmallFace", label: "Double Egress Small Face", v: spec.doorFrameProfile.doubleEgressSmallFace},
        {key: "drywallLip", label: "Drywall Lip", v: spec.doorFrameProfile.drywallLip},
        {key: "drywallHookDrop", label: "Drywall Hook Drop", v: spec.doorFrameProfile.drywallHookDrop},
        {key: "kerfDepth", label: "Kerf Depth", v: spec.doorFrameProfile.kerfDepth},
        {key: "expFace", label: "Exp Face", v: spec.doorFrameProfile.expFace},
    ];

    return all.filter(p => isRelevant(p.key, spec, profileKeys));
}

function textProps(spec: MdFrameSpec): TextProp[] {
    return [
        {key: "profile", label: "Profile", value: spec.profile},
        {key: "handing", label: "Handing", value: spec.handing || "(none)"},
        {key: "locationTransition", label: "Location Transition", value: spec.locationTransition || "(none)"},
        {key: "isPair", label: "Is Pair", value: spec.isPair ? "yes" : "no"},
        {key: "flipDoubleActiveHanding", label: "Flip Double Active Handing", value: spec.flipDoubleActiveHanding ? "yes" : "no"},
    ];
}

export function PropertiesDialog(props: {
    app: ElevationApp;
    onClose: () => void;
}) {
    const spec = props.app.frameSpec;
    const sys: UnitSystem = props.app.project?.unitSystem || "imperial";

    const profileKeys = useMemo(() => referencedProfileKeys(props.app), [props.app, spec]);
    const numerics = useMemo(() => numericProps(spec, profileKeys), [spec, profileKeys]);
    const texts = useMemo(() => textProps(spec), [spec]);

    // key of the row currently being edited (null = none)
    const [editing, setEditing] = useState<string | null>(null);

    // re-render whenever any FrmVar emits, so edits and downstream calculated
    // values stay live in the table.
    const [, bump] = useReducer((x: number) => x + 1, 0);
    useEffect(() => {
        const subs = numerics.map(n => n.v.subscribe(() => bump()));
        return () => subs.forEach(s => s.cancel());
    }, [numerics]);

    return (
        <Dialog open onClose={props.onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{py: 1.5}}>Frame Properties</DialogTitle>
            <DialogContent dividers sx={{p: 0}}>
                <table style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 12,
                    tableLayout: "fixed",
                }}>
                    <tbody>
                        <SectionHeader label="Configuration" />
                        {texts.map(t => (
                            <tr key={t.key} style={ROW_STYLE}>
                                <td style={NAME_CELL}>{t.label}</td>
                                <td style={VALUE_CELL}>{t.value}</td>
                                <td style={TAG_CELL} />
                            </tr>
                        ))}

                        <SectionHeader label="Dimensions" />
                        {numerics.map(n => {
                            const calculated = n.v.isCalculated();
                            const editable = !calculated;
                            const isEditing = editing === n.key;

                            return (
                                <tr key={n.key} style={ROW_STYLE}>
                                    <td style={NAME_CELL}>{n.label}</td>
                                    <td
                                        style={{
                                            ...VALUE_CELL,
                                            cursor: editable ? "pointer" : "default",
                                            color: editable && n.v.isModified() ? "#1565c0" : undefined,
                                            position: "relative",
                                        }}
                                        onClick={() => {
                                            if (editable) setEditing(n.key);
                                        }}
                                    >
                                        {formatDim(n.v.get(), sys)}
                                        {isEditing && (
                                            <DimensionEditor
                                                value={n.v.get()}
                                                sys={sys}
                                                locked={n.v.isModified()}
                                                onLockChange={() => {
                                                    n.v.userClearModified();
                                                    setEditing(null);
                                                }}
                                                onDone={(value: number) => {
                                                    n.v.userSet(value, {debug: "properties-edit"});
                                                    setEditing(null);
                                                }}
                                                onCancel={() => setEditing(null)}
                                            />
                                        )}
                                    </td>
                                    <td style={TAG_CELL}>
                                        {calculated ? (
                                            <Chip label="calc" size="small" variant="outlined" color="default"
                                                  sx={CHIP_STYLE} />
                                        ) : (
                                            <Chip label="edit" size="small" variant="outlined" color="primary"
                                                  sx={CHIP_STYLE} />
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </DialogContent>
            <DialogActions>
                <Button onClick={props.onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}

function SectionHeader(props: {label: string}) {
    return (
        <tr>
            <td colSpan={3} style={{
                padding: "6px 16px 2px",
                fontSize: 11,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: 0.5,
                color: "#888",
                background: "#fafafa",
            }}>
                {props.label}
            </td>
        </tr>
    );
}

const ROW_STYLE: React.CSSProperties = {
    borderTop: "1px solid #eee",
};

const NAME_CELL: React.CSSProperties = {
    padding: "3px 8px 3px 16px",
    color: "#555",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
};

const VALUE_CELL: React.CSSProperties = {
    padding: "3px 8px",
    fontFamily: "monospace",
    textAlign: "right",
    whiteSpace: "nowrap",
};

const TAG_CELL: React.CSSProperties = {
    padding: "3px 16px 3px 8px",
    width: 56,
    textAlign: "right",
};

const CHIP_STYLE = {
    height: 18,
    fontSize: 10,
    "& .MuiChip-label": {px: 0.75},
};
